import type { CreateOrderSheetArgs, OptionInfo } from '../../type/common.type';
import { checkZigzinOrVendorFlexDeliveryType } from './zigzin-checker';

/**
 * 주문 아이템들의 배송 타입 분석 결과
 */
export interface ShippingTypeAnalysis {
  /** 직진 배송 아이템이 포함되어 있는지 */
  hasZigzinItems: boolean;
  /** 일반 배송 아이템이 포함되어 있는지 */
  hasGeneralItems: boolean;
  /** 직진 배송 아이템 개수 */
  zigzinItemCount: number;
  /** 일반 배송 아이템 개수 */
  generalItemCount: number;
  /** 전체 아이템 개수 */
  totalItemCount: number;
}

/**
 * payload.order_item_list의 product_item_id를 사용해서
 * option.matched_item_list에서 직진/일반 배송 아이템을 체크하는 함수
 *
 * @param payload - 주문 데이터
 * @param option - 옵션 정보
 * @returns 배송 타입 분석 결과
 */
export const analyzeShippingTypes = (payload: CreateOrderSheetArgs, option: OptionInfo): ShippingTypeAnalysis => {
  const result: ShippingTypeAnalysis = {
    hasZigzinItems: false,
    hasGeneralItems: false,
    zigzinItemCount: 0,
    generalItemCount: 0,
    totalItemCount: payload.order_item_list.length,
  };

  // matched_item_list가 없으면 빈 결과 반환
  if (!option.catalog_product.matched_item_list || option.catalog_product.matched_item_list.length === 0) {
    return result;
  }

  // product_item_id로 빠른 조회를 위한 Map 생성
  const itemMap = new Map(option.catalog_product.matched_item_list.map((item) => [item.id, item]));

  // 각 주문 아이템의 배송 타입 체크
  for (const orderItem of payload.order_item_list) {
    const matchedItem = itemMap.get(orderItem.product_item_id);

    if (!matchedItem) {
      // matched_item_list에서 찾을 수 없는 경우
      continue;
    }

    // 배송 타입 판별
    const isZigzinDelivery = checkZigzinOrVendorFlexDeliveryType(matchedItem.delivery_type);

    if (isZigzinDelivery) {
      result.hasZigzinItems = true;
      result.zigzinItemCount++;
    } else {
      result.hasGeneralItems = true;
      result.generalItemCount++;
    }
  }

  return result;
};

/**
 * 직진 배송 아이템만 포함되어 있는지 체크
 */
export const hasOnlyZigzinItems = (analysis: ShippingTypeAnalysis): boolean => {
  return analysis.hasZigzinItems && !analysis.hasGeneralItems;
};

/**
 * 일반 배송 아이템만 포함되어 있는지 체크
 */
export const hasOnlyGeneralItems = (analysis: ShippingTypeAnalysis): boolean => {
  return analysis.hasGeneralItems && !analysis.hasZigzinItems;
};

/**
 * 직진과 일반 배송이 혼재되어 있는지 체크
 */
export const hasMixedShippingTypes = (analysis: ShippingTypeAnalysis): boolean => {
  return analysis.hasZigzinItems && analysis.hasGeneralItems;
};

/**
 * 주문에서 직진 배송 아이템이 포함되어 있는지 간단히 체크하는 함수
 */
export const checkHasZigzinItemsInOrder = (payload: CreateOrderSheetArgs, option: OptionInfo): boolean => {
  const analysis = analyzeShippingTypes(payload, option);
  return analysis.hasZigzinItems;
};

/**
 * 주문에서 일반 배송 아이템이 포함되어 있는지 간단히 체크하는 함수
 */
export const checkHasGeneralItemsInOrder = (payload: CreateOrderSheetArgs, option: OptionInfo): boolean => {
  const analysis = analyzeShippingTypes(payload, option);
  return analysis.hasGeneralItems;
};
